<template>
    <div class="warning-backdrop" @click.self="$emit('cancel')">
        <div class="card">
            <div class="header">
                <div class="image">
                    <svg
                        aria-hidden="true"
                        stroke="currentColor"
                        stroke-width="1.5"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                            stroke-linejoin="round"
                            stroke-linecap="round"
                        ></path>
                    </svg>
                </div>
                <div class="content">
                    <span class="title">{{ title }}</span>
                    <div class="message">
                        <slot name="message">
                            <template v-if="itemName || warningMessage">
                                <p v-if="itemName" style="margin-bottom: 0.25rem;">Are you sure you want to delete <strong>"{{ truncatedItemName }}"</strong>?</p>
                                <p v-if="warningMessage" style="margin-top: 0;">{{ warningMessage }}</p>
                            </template>
                            <template v-else>
                                <p style="margin: 0;">{{ message }}</p>
                            </template>
                        </slot>
                    </div>
                </div>
                <div class="actions">
                    <button 
                        class="deactivate" 
                        type="button"
                        @click="handleConfirm"
                        v-if="!hideAction"
                        :disabled="isLoading"
                    >
                        <span v-if="isLoading">
                            <i class="fas fa-spinner fa-spin me-1"></i> Processing...
                        </span>
                        <span v-else>
                            {{ confirmText || actionText }}
                        </span>
                    </button>
                    <button 
                        class="cancel" 
                        type="button"
                        @click="$emit('cancel')"
                        :disabled="isLoading"
                    >
                        {{ cancelText }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OWarningBox',
    props: {
        title: {
            type: String,
            default: 'Warning'
        },
        message: {
            type: String,
            default: 'Are you sure you want to proceed with this action?'
        },
        itemName: {
            type: String,
            default: ''
        },
        warningMessage: {
            type: String,
            default: ''
        },
        actionText: {
            type: String,
            default: 'Proceed'
        },
        confirmText: {
            type: String,
            default: ''
        },
        cancelText: {
            type: String,
            default: 'Cancel'
        },
        hideAction: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    emits: ['action', 'confirm', 'cancel'],
    computed: {
        truncatedItemName() {
            if (!this.itemName) return '';
            return this.itemName.length > 50 ? this.itemName.substring(0, 50) + '...' : this.itemName;
        }
    },
    methods: {
        handleConfirm() {
            this.$emit('action');
            this.$emit('confirm');
        }
    }
}
</script>

<style scoped>
.warning-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5); /* semi-transparent backdrop */
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000; /* pastikan muncul di atas konten lain */
}

.card {
    width: 90%;
    max-width: 400px; /* sedikit lebih besar untuk responsivitas */
    margin: 1rem;
    overflow: hidden;
    position: relative;
    background-color: #ffffff;
    text-align: left;
    border-radius: 0.5rem;
    box-shadow:
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 10px 10px -5px rgba(0, 0, 0, 0.04);
    animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
    from {
        transform: translateY(-20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.header {
    padding: 1.25rem 1rem 1rem 1rem;
    background-color: #ffffff;
}

.image {
    display: flex;
    margin-left: auto;
    margin-right: auto;
    background-color: var(--danger-bg, #fee2e2);
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: 9999px;
}

.image svg {
    color: var(--danger-color, #dc2626);
    width: 1.5rem;
    height: 1.5rem;
}

.content {
    margin-top: 0.75rem;
    text-align: center;
}

.title {
    color: #111827;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
}

.message {
    margin-top: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
    line-height: 1.25rem;
}

.actions {
    margin: 0.75rem 1rem;
    background-color: #f9fafb;
}

.deactivate {
    display: inline-flex;
    padding: 0.5rem 1rem;
    background-color: var(--danger-color, #dc2626);
    color: #ffffff;
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 500;
    justify-content: center;
    width: 100%;
    border-radius: 0.375rem;
    border-width: 1px;
    border-color: transparent;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    transition: all 0.2s;
}

.deactivate:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.cancel {
    display: inline-flex;
    margin-top: 0.75rem;
    padding: 0.5rem 1rem;
    background-color: #ffffff;
    color: #374151;
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 500;
    justify-content: center;
    width: 100%;
    border-radius: 0.375rem;
    border: 1px solid #d1d5db;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    transition: all 0.2s;
}

.cancel:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

button {
    cursor: pointer;
}

.deactivate:hover {
    background-color: var(--danger-light, #b91c1c);
}

.cancel:hover {
    background-color: #f3f4f6;
}
</style>
